import React, { useState, useEffect } from 'react';
import Link from 'next/link';

import LinearProgress from '@mui/material/LinearProgress';
import Box from '@mui/material/Box';

import { useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import { useQuery, useMutation } from 'react-query';
import { useForm } from 'react-hook-form';
import { isUserLoggedIn, getAccessToken } from 'utils/authentication';
import UseToast from 'hooks/use-toast';

import {
	CourseCertificateService,
	LectureCertificateService,
	ContentCertificatePdfService,
	promotedLecture
} from 'services/core';
import { UpdateFullnameService, userProfileService } from 'services/auth';

import TextInput from '@core/components/textinput/TextInput';
import Spinner from '@core/components/spinner/Spinner';
import ActionButton from '@core/components/actionbutton/ActionButton';
import LinkButton from '@core/components/linkbutton/LinkButton';

import CourseBadgeIcon from 'assets/icons/course-badge-icon.svg';
import CourseBadgePremiumIcon from 'assets/icons/course-badge-icon-premium.svg';
import TickIcon from 'assets/icons/tick.svg';
import PremiumBenefitsImage from 'assets/images/pages/course/premium-benefits.png';
import ArrowrightIcon from 'assets/icons/arrow-right.svg';
import CourseSertificateBlured from 'assets/images/pages/course/course-certificate-blured.png';
import CertificateThumb from 'assets/images/pages/course/certificate-thumb.png';
import { Award, Mobile, WifiSquare, Share } from 'iconsax-react';

import classes from './ContentCertificate.module.scss';




const ContentCertificate = ({ content_type }) => {
	const router = useRouter();
	const hasSubscription = useSelector(state => state.user.hasSubscription);
	const [progress, setProgress] = React.useState(0);
	const [courseId, setCourseId] = useState(undefined);
	const [userHasName, setUserHasName] = useState(undefined);
	const [showProgress, setShowProgress] = useState(true);
	useEffect(() => {
		if (router.isReady) {
			setCourseId(router?.query?.slug[0]);
		}
	}, []);
	const { ToastContainer, toast } = UseToast();
	// call certificate api
	const { data, error, isLoading, isFetching, refetch } = useQuery(
		['content_certificate', courseId, content_type],
		() =>
			content_type === 'lecture'
				? LectureCertificateService(courseId)
				: CourseCertificateService(courseId),
		{
			// refetchOnMount: 'always',
			enabled: false,
		}
	);
	//get random course data to show in the modal
	const { data: promotedCourseData } = useQuery(['promoted-course-data'], promotedLecture);
	// get user data
	const { data: userData, isLoading: userDataIsLoading, isFetching: userIsFetching, refetch: refetchUserData } = useQuery(
		['user-profile-data', isUserLoggedIn(), getAccessToken()],
		userProfileService,
		{
			onSuccess: (userDataSuccess) => {
				if (userDataSuccess?.data?.is_name_verified) {
					setUserHasName(true);
					refetch();
				}
				else
					setUserHasName(false);
			},
		}
	);
	// call update fullname api
	const { mutate: updateFullName } = useMutation(UpdateFullnameService, {
		onSuccess: () => {
			refetchUserData();
		}
	});

	const { mutate: getCertificatePdf } = useMutation(ContentCertificatePdfService, {
		onSuccess: (data) => {
			window.open(data.data.certificate, "_blank");
		}
	});
	const { mutate: getCertificateImage } = useMutation(ContentCertificatePdfService, {
		onSuccess: (data) => {
			window.open(data.data.certificate_image, "_blank");
		}
	});

	const {
		register,
		handleSubmit,
		formState: { errors, isDirty, isValid }
	} = useForm({ mode: 'onChange' });

	// handle fullname form submit
	const onSubmitName = (fullnameData) => {
		updateFullName(fullnameData);
	};
	useEffect(() => {
		const timer = setInterval(() => {
			setProgress((oldProgress) => {
				if (oldProgress === 100) {
					clearInterval(timer);
					return 100;
				}
				const diff = Math.random() * 20;
				return Math.min(oldProgress + diff, 100);
			});
		}, 500);
	}, []);

	useEffect(() => {
		if (data && !isLoading && !isFetching) {
			setProgress(100);
		}
		else if (isLoading || isFetching)
			setProgress(0);
	}, [data, isFetching]);
	useEffect(() => {
		if (progress === 100) {
			const timer = setTimeout(() => {
				setShowProgress(false);
			}, 400);
			return () => clearTimeout(timer);
		}
	}, [progress]);


	const contentType_text = content_type === 'lecture' ? 'course' : 'job qualification';

	return (
		<>
			<ToastContainer />
			<div className={`${classes['content-certificate-wrapper']}`}>
				{userData && !userDataIsLoading && !userIsFetching && userHasName ? (
					<div className="row">
						<div className="col-md-6 col-12 ">
							<div className='d-flex flex-column align-items-center justify-content-center gap-3 h-100'>
								{showProgress ? (
									<>
										<img className={classes['certificate-image']} src={CourseSertificateBlured.src} alt={'certificate'} />
										<Box className='d-none d-md-block' sx={{ width: '100%', maxWidth: '266px', minHeight: '58px' }}>
											<div className='w-100 d-flex justify-content-between'>
												<span className={classes['progress-percentage']}>
													{`${Math.round(progress)}%`}
												</span>
												<span className={classes['progress-status']}>
													Preparing
												</span>
											</div>
											<LinearProgress variant="determinate" value={progress} />
										</Box>
										<div className='d-flex d-md-none justify-content-center align-items-center w-100'>
											<img src={CertificateThumb.src} alt='certificate thumbnail' />
											<Box sx={{ width: '80%', maxWidth: '300px', minHeight: '58px' }}>
												<div className='w-100 d-flex justify-content-between'>
													<span className={classes['progress-percentage']}>
														{`${Math.round(progress)}%`}
													</span>
													<span className={classes['progress-status']}>
														Preparing
													</span>
												</div>
												<LinearProgress variant="determinate" value={progress} />
											</Box>
										</div>
									</>

								) : (
									<>
										{(data && !isLoading && !isFetching) && (
											<>
												{(data.data.certificate_id) ? (
													<>
														<img className={classes['certificate-image']} src={data?.data.certificate_thumbnail} alt={data?.data.title} />
														<div className={`${classes['content-certificate-result__actions']} d-flex gap-2`}>
															<img src={CertificateThumb.src} alt='certificate thumbnail' className='d-block d-md-none' />
															<ActionButton kind='secondary' onClick={() => getCertificatePdf(data?.data.certificate_id)}>
																Save as PDF
															</ActionButton>
															<ActionButton kind='secondary' onClick={() => getCertificateImage(data?.data.certificate_id)}>
																Save as JPEG
															</ActionButton>
														</div>
													</>
												) : (
													<>
														<img className={classes['certificate-image']} src={CourseSertificateBlured.src} alt={'certificate'} />
													</>
												)}
											</>
										)}
									</>
								)}
							</div>
						</div>
						<div className={`${classes['content-certificate-result']} col-md-6 col-12 pe-3`}>
							{hasSubscription ? (
								<>
									<div className={`${classes['head']} d-none d-md-flex align-items-center gap-2`}>
										<CourseBadgePremiumIcon />
										<h2>Continue Your Learning Journey</h2>
									</div>
									<div className={classes['course-details']}>
										<h3>{promotedCourseData?.data.title}</h3>
										<Link href={'/pharmuni-certificate'} rel="noopener noreferrer" target="_blank" className="d-flex align-items-center gap-1 mb-3 px-3">
											<Award size={24} variant="TwoTone" />
											<h5>+ Certificate</h5>
										</Link>
										<div className="d-flex align-items-center gap-0 mb-3 px-3">
											<Mobile className='ms-1' size={24} variant="TwoTone" />
											<h5>available on app</h5>
										</div>
										<div className="d-flex align-items-center gap-1 mb-3 px-3">
											<WifiSquare variant="TwoTone" />
											<h5>Online at your own pace</h5>
										</div>
										<div className="d-flex align-items-center gap-1 px-3">
											<Share variant="TwoTone" />
											<h5>Shareable certificate of completion</h5>
										</div>
										<div className='d-flex justify-content-end'>
											<LinkButton kind='simple' to={`/courses/${promotedCourseData?.data?.id}/${promotedCourseData?.data.slug}`} target="_blank" rel="noopener noreferrer">
												Start Learning <ArrowrightIcon />
											</LinkButton>
										</div>
									</div>
									<LinkButton kind='primary-color' to={'/explore'} target="_blank" rel="noopener noreferrer">
										Explore 40+ courses
									</LinkButton>
								</>
							) : (
								<>
									{(data && !isLoading && !isFetching) && (
										<>
											{(data.data.certificate_id) ? (
												<>
													<div className={`${classes['head']} d-none d-md-flex align-items-center gap-2`}>
														<CourseBadgeIcon />
														<h2>Unlock Your Full Pharma Potential!</h2>
													</div>
													<div className={classes['premium-items']}>
														<div className="d-flex align-items-center gap-1">
															<TickIcon />
															<h5>40+ Premium Courses</h5>
														</div>
														<div className="d-flex align-items-center gap-1">
															<TickIcon />
															<h5>Certificates by ZPS (TUV-ISO 9001 certified)</h5>
														</div>
														<div className="d-flex align-items-center gap-1">
															<TickIcon />
															<h5>Monthly Free VIP Coaching session</h5>
														</div>
													</div>
													<img className={classes['premium-image']} src={PremiumBenefitsImage.src} alt='premium benefits image' />
													<LinkButton kind='primary-color' to={'/plans'} target="_blank" rel="noopener noreferrer">
														Unlock Your Growth Path
													</LinkButton>
												</>
											) : (
												<>
													<div className={`${classes['head']} d-none d-md-flex flex-column justify-content-center text-center align-items-center gap-2`}>
														<CourseBadgeIcon />
														<h2>Unlock Your Certificate with Pharmuni Premium</h2>
													</div>
													<div className={classes['premium-items']}>
														<p>Certificates for all courses are a Premium benefit, upgrade to download yours now. With Premium subscription you will also get:</p>
														<div className="d-flex align-items-center gap-1">
															<TickIcon />
															<h5>Unlimited access to 50+ pharma courses.</h5>
														</div>
														<div className="d-flex align-items-center gap-1">
															<TickIcon />
															<h5>SO 9001–backed, verifiable certificate.</h5>
														</div>
														<div className="d-flex align-items-center gap-1">
															<TickIcon />
															<h5>Monthly VIP 1:1 consultation. </h5>
														</div>
														<div className="d-flex align-items-center gap-1">
															<TickIcon />
															<h5>Career Paths & Skill Tree. </h5>
														</div>
														<div className="d-flex align-items-center gap-1">
															<TickIcon />
															<h5>Pharma job board access. </h5>
														</div>
													</div>
													<LinkButton kind='primary-color' to={'/plans'} target="_blank" rel="noopener noreferrer">
														See Plans & Unlock Certificate
													</LinkButton>
												</>
											)}
										</>
									)}
								</>
							)}
						</div>
					</div>

				) : userData && !userDataIsLoading && !userIsFetching && !userHasName ? (
					<div
						className={`${classes['content-certificate-verify-name']} w-100 d-flex flex-column`}>
						<h1>Congratulations on completing your {contentType_text}</h1>
						<p>
							One thing before getting to your certificate. Enter your name below. Please note
							that your name is not editable later
						</p>
						<form onSubmit={handleSubmit(onSubmitName)}>
							<TextInput
								validation={{
									required: true
								}}
								input={{
									type: 'text',
									label: 'First Name',
									name: 'first_name',
									placeholder: 'First Name',
									id: 'first_name'
								}}
								errors={errors}
								register={register}
							/>
							<TextInput
								validation={{
									required: true
								}}
								input={{
									type: 'text',
									label: 'Last Name',
									name: 'last_name',
									placeholder: 'Last Name',
									id: 'last_name'
								}}
								errors={errors}
								register={register}
							/>
							<label className={`${classes['verify-name-checkbox']} `} htmlFor="verify">
								<p>
									I verify that this is my full name for the use in my account and on my
									certificates.
								</p>
								<input
									type="checkbox"
									name="verify"
									id="verify"
									{...register('verify', {
										required: true
									})}
								/>
								<span></span>
							</label>
							<button type="submit" className="w-100" disabled={!isDirty || !isValid}>
								Submit
							</button>
						</form>
					</div>
				) : (
					<div
						className={`${classes['content_certificate_Loading']}  d-flex align-items-center justify-content-center`}>
						<Spinner />
					</div>
				)}
			</div>
		</>
	);
};

export default ContentCertificate;
